import React from "react"
import { Button } from "@/components/ui/button"

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { useDropzone } from "react-dropzone"

import { Input } from "@/components/ui/input"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

import useActiveHash from "@/hooks/useActiveHash"

import { Link, useNavigate, useParams } from "react-router-dom"

import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { APP_ROUTES } from "@/common/urls"
import { Checkbox } from "@/components/ui/checkbox"
import {
    FILE_MIME_TYPES,
    FileMimeType,
    useApplicationSettings,
    useFetchApplicantApplication,
    useUpdateApplicantApplication,
    useUploadFiles,
} from "@/api/applicationQueries"
import { Loader, TriangleAlert, X } from "lucide-react"
import { Label } from "@/components/ui/label"
import { Timestamp } from "firebase/firestore"
import { ApplicationDeadlineModal } from "@/components/ApplicationDeadlineSettings/ApplicationDeadlineModal"
import { useGetUserById } from "@/api/userQueries"

// Select Lists

const scotlandRegions = [
    "Edinburgh",
    "Glasgow",
    "Dundee",
    "Aberdeen",
    "Highlands and Islands",
    "North East",
    "Fife and Kinross",
    "Dumfries and Galloway",
    "Central",
    "West of Scotland",
    "Lothian and Borders",
    "Region wide",
    "Country wide",
]

const canadaRegions = [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Nova Scotia",
    "Ontario (excluding Toronto)",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
    "Territories",
    "Toronto",
    "Region wide",
    "Country wide",
]

const scotlandTerritories = [
    "Highland",
    "Orkney",
    "Shetland",
    "Western Isles",
    "Aberdeenshire",
    "Moray",
    "Angus",
    "Fife",
    "Perth and Kinross",
    "Clackmannanshire",
    "Stirling",
    "Falkirk",
    "Argyll and Bute",
    "East Ayrshire",
    "North Ayrshire",
    "South Ayrshire",
    "East Dunbartonshire",
    "West Dunbartonshire",
    "East Renfrewshire",
    "Renfrewshire",
    "Inverclyde",
    "North Lanarkshire",
    "South Lanarkshire",
    "East Lothian",
    "Midlothian",
    "West Lothian",
    "Scottish Borders",
]

const canadaTerritories = ["North West Territories", "Nunavut", "Yukon"]

const INELIGIBLE_TURNOVER_AMOUNT = "5,000,000"
const INELIGIBLE_TURNOVER_LABEL = `> £${INELIGIBLE_TURNOVER_AMOUNT}`

const turnoverOptions = [
    "< £100,000",
    "£100,001 - £500,000",
    "£500,001 - £1,000,000",
    `£1,000,001 - £${INELIGIBLE_TURNOVER_AMOUNT}`,
    INELIGIBLE_TURNOVER_LABEL,
] as const

const ageGroups = ["Youth", "Elderly", "Youth and Elderly"]
const sectors = ["Education", "Health", "Social", "Arts & Music", "Play"]
const subsectors = [
    "Personal development/new skills",
    "Literacy",
    "IT/computers",
    "Financial/benefits",
    "Mental health",
    "Drug and alcohol dependency",
    "Rehabilitation",
    "Exercise",
    "Hospice",
    "Housing",
    "Befriending",
    "Community Groups",
    "Environment/gardening",
]

const hearOf = [
    "OSCR Website",
    "Web Search",
    "Invited by Trustee",
    "Recommended by Friend / Acquaintance",
] as const

// Form schemas
export const orgDetailsSchema = z.object({
    orgName: z.string().min(1, "You must enter a organisation name"),
    orgURL: z.string().url({ message: "Please enter a valid website URL" }),
    estTurnover: z
        .enum(turnoverOptions, { message: "You must select an option" })
        .superRefine((val, ctx) => {
            if (val === INELIGIBLE_TURNOVER_LABEL) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        "Unfortunately, your organisation is not eligible for application at this time",
                })
            }
        }),
    region: z.string().min(1, "You must select an option"),
    territory: z.string().min(1, "You must select an option"),
    receivedGrantBefore: z.string().min(1, "You must select an option"),
})

export const orgBeneficiariesSchema = z.object({
    ageGroup: z.string().min(1, "You must select an option"),
    veteransCheck: z.optional(z.boolean()),
    sector: z.string().min(1, "You must select an option"),
    subsector: z.string().min(1, "You must select an option"),
})

export const applicationDetailsSchema = z.object({
    requestedAmount: z.coerce.number().refine((value) => value > 0, {
        message: "You must enter an amount greater than 0",
    }),
    applicationReason: z.string().min(1, "You must enter a reason"),
    orgAims: z.string().min(1, "You must enter the aim of the organisation"),
    hearOf: z.string().min(1, "You must select an option"),
})

type AccountFile = {
    file: File
    isValid: boolean
}

export function CreateApplication() {
    const navFunc = useNavigate()
    const [accountFiles, setAccountFiles] = useState<AccountFile[]>([])
    const [showDeadlineModal, setShowDeadlineModal] = useState(false)

    // get the :id from the url
    const { id } = useParams()
    const userDetails = useGetUserById(navFunc)

    const { data: applicationData, isFetching: applicationDataLoading } =
        useFetchApplicantApplication(id || "")

    const { mutateAsync: updateApplication, isPending: isApplicationUpdating } =
        useUpdateApplicantApplication(id || "")

    const { mutateAsync: uploadFiles, isPending: isFileUploadPending } =
        useUploadFiles(id || "")

    const { data: settings } = useApplicationSettings()

    const activeHash = useActiveHash()
    const orgDetailsRef = useRef<HTMLDivElement>(null)
    const orgBeneficiariesRef = useRef<HTMLDivElement>(null)
    const applicationDetailsRef = useRef<HTMLDivElement>(null)
    const supportingDocumentsRef = useRef<HTMLDivElement>(null)
    const finalSubmission = useRef<HTMLDivElement>(null)

    const handleScroll = (ref: React.RefObject<HTMLDivElement>) => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: "smooth" })
        }
    }

    const orgDetailsForm = useForm<z.infer<typeof orgDetailsSchema>>({
        resolver: zodResolver(orgDetailsSchema),
        defaultValues: {
            orgName: "",
            orgURL: "",
            // estTurnover: "",
            region: "",
            territory: "",
            receivedGrantBefore: "no",
        },
    })

    const orgBeneficiariesForm = useForm<
        z.infer<typeof orgBeneficiariesSchema>
    >({
        resolver: zodResolver(orgBeneficiariesSchema),
        defaultValues: {
            ageGroup: "",
            veteransCheck: false,
            sector: "",
            subsector: "",
        },
    })

    const applicationDetailsForm = useForm<
        z.infer<typeof applicationDetailsSchema>
    >({
        resolver: zodResolver(applicationDetailsSchema),
        defaultValues: {
            requestedAmount: 0,
            applicationReason: "",
            orgAims: "",
            hearOf: "",
        },
    })

    // Show deadline modal on mount if there's a deadline
    useEffect(() => {
        if (
            userDetails.data?.role !== "admin" &&
            (settings?.deadline || settings?.isAcceptingApplications === false)
        ) {
            setShowDeadlineModal(true)
        }
    }, [settings, userDetails.data?.role])

    async function onSubmitOrgDetails(
        values: z.infer<typeof orgDetailsSchema>
    ) {
        await updateApplication(values)
    }

    async function onSubmitOrgBeneficiaries(
        values: z.infer<typeof orgBeneficiariesSchema>
    ) {
        await updateApplication(values)
    }

    async function onSubmitApplicationDetails(
        values: z.infer<typeof applicationDetailsSchema>
    ) {
        await updateApplication(values)
    }

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const newFiles = acceptedFiles.map((file) => ({
            file,
            isValid: FILE_MIME_TYPES.includes(file.type as FileMimeType),
        }))

        setAccountFiles((prevFiles) => [...prevFiles, ...newFiles])
    }, [])

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
    })

    function removeFile(fileName: string) {
        setAccountFiles((prevFiles) =>
            prevFiles.filter(({ file }) => file.name !== fileName)
        )
    }

    async function handleFileUpload() {
        const validFiles = accountFiles
            .filter(({ isValid }) => isValid)
            .map(({ file }) => file)

        if (validFiles.length > 0) {
            await uploadFiles(validFiles)
            setAccountFiles((prevFiles) =>
                prevFiles.filter(({ isValid }) => !isValid)
            )
            return
        }

        return
    }

    async function onSubmitApplicationForReview() {
        if (settings?.isAcceptingApplications === false) {
            setShowDeadlineModal(true)
            return
        }
        try {
            const orgDetailsValid = await orgDetailsForm.trigger()
            const orgBeneficiariesValid = await orgBeneficiariesForm.trigger()
            const applicationDetailsValid =
                await applicationDetailsForm.trigger()

            const isValid =
                orgDetailsValid &&
                orgBeneficiariesValid &&
                applicationDetailsValid &&
                applicationData
            const hasApplicantFiles =
                applicationData?.applicantFiles?.length !== 0

            if (isValid && hasApplicantFiles) {
                await updateApplication({
                    ...orgDetailsForm.getValues(),
                    ...orgBeneficiariesForm.getValues(),
                    ...applicationDetailsForm.getValues(),
                    status: "submitted",
                    dateSubmitted: Timestamp.now(),
                })
            }
        } catch (error) {
            throw new Error("Error submitting application: " + error)
        }
    }

    useEffect(() => {
        if (applicationData) {
            orgDetailsForm.reset({
                orgName: applicationData.orgName || "",
                orgURL: applicationData.orgURL || "",
                estTurnover:
                    (applicationData.estTurnover as Exclude<
                        (typeof turnoverOptions)[number],
                        typeof INELIGIBLE_TURNOVER_LABEL
                    >) || undefined,
                region: applicationData.region || "",
                territory: applicationData.territory || "",
                receivedGrantBefore:
                    applicationData.receivedGrantBefore || "no",
            })

            orgBeneficiariesForm.reset({
                ageGroup: applicationData.ageGroup || "",
                veteransCheck: applicationData.veteransCheck || false,
                sector: applicationData.sector || "",
                subsector: applicationData.subsector || "",
            })

            applicationDetailsForm.reset({
                requestedAmount: applicationData.requestedAmount || 0,
                applicationReason: applicationData.applicationReason || "",
                orgAims: applicationData.orgAims || "",
                hearOf: applicationData.hearOf || "",
            })
        }
    }, [
        applicationData,
        orgDetailsForm,
        orgBeneficiariesForm,
        applicationDetailsForm,
    ])

    const isSubmitted = useMemo(
        () => applicationData?.status === "submitted",
        [applicationData]
    )

    return (
        <>
            <ApplicationDeadlineModal
                open={showDeadlineModal}
                onOpenChange={setShowDeadlineModal}
            />
            <div className="flex min-h-screen flex-col pb-10 bg-muted/40">
                {!!settings?.deadline && (
                    <span className="text-sm py-2 px-12 w-full flex items-center justify-center bg-yellow-500/10 text-orange-500">
                        <TriangleAlert className="inline-block mr-2 text-orange-500 flex-shrink-0" />
                        New submissions are going to be accepted until{" "}
                        {settings?.deadline?.toDate().toLocaleTimeString()} on{" "}
                        {settings?.deadline?.toDate().toLocaleDateString()}
                    </span>
                )}
                {settings?.isAcceptingApplications === false && (
                    <span className="text-sm py-2 px-12 w-full flex items-center justify-center bg-red-500/10 text-red-500">
                        <TriangleAlert className="inline-block mr-2 text-red-500 flex-shrink-0" />
                        New submissions are no longer being accepted at this
                        time
                    </span>
                )}
                <main className="flex relative min-h-[calc(100vh_-_theme(spacing.16))] flex-1 flex-col gap-4 p-4 md:gap-8 md:p-10">
                    <div className="mx-auto flex justify-start items-center w-full max-w-6xl gap-6">
                        <h1 className="text-3xl font-semibold">Application</h1>
                    </div>
                    <div className="mx-auto grid w-full max-w-6xl items-start gap-6 md:grid-cols-[180px_1fr] lg:grid-cols-[250px_1fr]">
                        <nav className="md:sticky top-24 grid gap-4 text-sm text-muted-foreground">
                            <a
                                href="#orgDetails"
                                onClick={() => handleScroll(orgDetailsRef)}
                                className={
                                    activeHash === "#orgDetails"
                                        ? "font-semibold text-primary underline"
                                        : ""
                                }
                            >
                                Organisation Details
                            </a>
                            <a
                                href="#orgBeneficiaries"
                                onClick={() =>
                                    handleScroll(orgBeneficiariesRef)
                                }
                                className={
                                    activeHash === "#orgBeneficiaries"
                                        ? "font-semibold text-primary underline"
                                        : ""
                                }
                            >
                                Organisation Beneficiaries
                            </a>
                            <a
                                href="#applicationDetails"
                                onClick={() =>
                                    handleScroll(applicationDetailsRef)
                                }
                                className={
                                    activeHash === "#applicationDetails"
                                        ? "font-semibold text-primary underline"
                                        : ""
                                }
                            >
                                Application Details
                            </a>
                            <a
                                href="#supportingDocuments"
                                onClick={() =>
                                    handleScroll(supportingDocumentsRef)
                                }
                                className={
                                    activeHash === "#supportingDocuments"
                                        ? "font-semibold text-primary underline"
                                        : ""
                                }
                            >
                                Supporting Documents
                            </a>
                            <a
                                href="#submitApplication"
                                onClick={() => handleScroll(finalSubmission)}
                                className={
                                    activeHash === "#submitApplication"
                                        ? "font-semibold text-primary underline"
                                        : ""
                                }
                            >
                                Complete Submission
                            </a>
                            {applicationDataLoading && (
                                <p className="font-semibold flex items-center justify-start gap-2 animate-pulse">
                                    <span>Loading Data</span>
                                    <Loader
                                        size={22}
                                        className="text-app-defaultLight animate-spin"
                                    />
                                </p>
                            )}
                        </nav>
                        <div className="grid gap-6">
                            {/* Organisation Details */}
                            <Card
                                id="orgDetails"
                                x-chunk="dashboard-04-chunk-1"
                            >
                                <CardHeader>
                                    <CardTitle>Organisation Details</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Form {...orgDetailsForm}>
                                        <form
                                            onSubmit={orgDetailsForm.handleSubmit(
                                                onSubmitOrgDetails
                                            )}
                                            className="space-y-8"
                                        >
                                            <FormField
                                                control={orgDetailsForm.control}
                                                name="orgName"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Organisation Name
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                placeholder="Organisation Name"
                                                                {...field}
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={orgDetailsForm.control}
                                                name="orgURL"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Organisation Website
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                placeholder="Organisation Website URL"
                                                                {...field}
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={orgDetailsForm.control}
                                                name="estTurnover"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Estimated Turnover
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Select
                                                                onValueChange={(
                                                                    ...args
                                                                ) => {
                                                                    // sideeffect: trigger validation on this field
                                                                    field.onChange(
                                                                        ...args
                                                                    )
                                                                    orgDetailsForm.trigger(
                                                                        "estTurnover"
                                                                    )
                                                                }}
                                                                value={
                                                                    field.value
                                                                }
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="Estimated Turnover" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {turnoverOptions.map(
                                                                        (
                                                                            turnover
                                                                        ) => (
                                                                            <SelectItem
                                                                                key={
                                                                                    turnover
                                                                                }
                                                                                value={
                                                                                    turnover
                                                                                }
                                                                            >
                                                                                {
                                                                                    turnover
                                                                                }
                                                                            </SelectItem>
                                                                        )
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={orgDetailsForm.control}
                                                name="region"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Region
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Select
                                                                onValueChange={
                                                                    field.onChange
                                                                }
                                                                value={
                                                                    field.value
                                                                }
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="City / Region" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {(applicationData?.country?.toLowerCase() ===
                                                                    "scotland"
                                                                        ? scotlandRegions
                                                                        : canadaRegions
                                                                    ).map(
                                                                        (
                                                                            region
                                                                        ) => (
                                                                            <SelectItem
                                                                                key={
                                                                                    region
                                                                                }
                                                                                value={
                                                                                    region
                                                                                }
                                                                            >
                                                                                {
                                                                                    region
                                                                                }
                                                                            </SelectItem>
                                                                        )
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={orgDetailsForm.control}
                                                name="territory"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Territory
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Select
                                                                onValueChange={
                                                                    field.onChange
                                                                }
                                                                value={
                                                                    field.value
                                                                }
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="Council / Territory" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {(applicationData?.country?.toLowerCase() ===
                                                                    "scotland"
                                                                        ? scotlandTerritories
                                                                        : canadaTerritories
                                                                    ).map(
                                                                        (
                                                                            region
                                                                        ) => (
                                                                            <SelectItem
                                                                                key={
                                                                                    region
                                                                                }
                                                                                value={
                                                                                    region
                                                                                }
                                                                            >
                                                                                {
                                                                                    region
                                                                                }
                                                                            </SelectItem>
                                                                        )
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={orgDetailsForm.control}
                                                name="receivedGrantBefore"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Have you received a
                                                            grant from us
                                                            before?
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Select
                                                                onValueChange={
                                                                    field.onChange
                                                                }
                                                                value={
                                                                    field.value
                                                                }
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="Received Grant Before" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="yes">
                                                                        Yes
                                                                    </SelectItem>
                                                                    <SelectItem value="no">
                                                                        No
                                                                    </SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <section className="flex w-full justify-end">
                                                <Button
                                                    className="px-6"
                                                    type="submit"
                                                    disabled={
                                                        isApplicationUpdating ||
                                                        isSubmitted
                                                    }
                                                >
                                                    Save Changes
                                                </Button>
                                            </section>
                                        </form>
                                    </Form>
                                </CardContent>
                            </Card>
                            {/* Organisation Beneficiaries */}
                            <Card
                                id="orgBeneficiaries"
                                x-chunk="dashboard-04-chunk-2"
                            >
                                <CardHeader>
                                    <CardTitle>
                                        Organisation Beneficiaries
                                    </CardTitle>
                                    <CardDescription>
                                        Select the groups and industries which
                                        most closely benefit from your
                                        organisations work. If the beneficiaries
                                        of the project for which you are
                                        applying is significantly different to
                                        those of your charity, please choose
                                        those which applies to the project.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Form {...orgBeneficiariesForm}>
                                        <form
                                            onSubmit={orgBeneficiariesForm.handleSubmit(
                                                onSubmitOrgBeneficiaries
                                            )}
                                            className="space-y-8"
                                        >
                                            <FormField
                                                control={
                                                    orgBeneficiariesForm.control
                                                }
                                                name="ageGroup"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Age Group
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Select
                                                                onValueChange={
                                                                    field.onChange
                                                                }
                                                                value={
                                                                    field.value
                                                                }
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="Age Group" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {ageGroups.map(
                                                                        (
                                                                            ageGroup
                                                                        ) => (
                                                                            <SelectItem
                                                                                key={
                                                                                    ageGroup
                                                                                }
                                                                                value={
                                                                                    ageGroup
                                                                                }
                                                                                className="capitalize"
                                                                            >
                                                                                {
                                                                                    ageGroup
                                                                                }
                                                                            </SelectItem>
                                                                        )
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={
                                                    orgBeneficiariesForm.control
                                                }
                                                name="veteransCheck"
                                                render={({ field }) => (
                                                    <FormItem className="flex flex-col justify-start items-start">
                                                        <FormLabel>
                                                            My organisation
                                                            works with Veterans
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Checkbox
                                                                checked={
                                                                    field.value
                                                                }
                                                                onCheckedChange={
                                                                    field.onChange
                                                                }
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={
                                                    orgBeneficiariesForm.control
                                                }
                                                name="sector"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Sector
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Select
                                                                onValueChange={
                                                                    field.onChange
                                                                }
                                                                value={
                                                                    field.value
                                                                }
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="Sector" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {sectors.map(
                                                                        (
                                                                            sector
                                                                        ) => (
                                                                            <SelectItem
                                                                                key={
                                                                                    sector
                                                                                }
                                                                                value={
                                                                                    sector
                                                                                }
                                                                                className="capitalize"
                                                                            >
                                                                                {
                                                                                    sector
                                                                                }
                                                                            </SelectItem>
                                                                        )
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={
                                                    orgBeneficiariesForm.control
                                                }
                                                name="subsector"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Sub Sector
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Select
                                                                onValueChange={
                                                                    field.onChange
                                                                }
                                                                value={
                                                                    field.value
                                                                }
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="Sub Sector" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {subsectors.map(
                                                                        (
                                                                            sector
                                                                        ) => (
                                                                            <SelectItem
                                                                                key={
                                                                                    sector
                                                                                }
                                                                                value={
                                                                                    sector
                                                                                }
                                                                                className="capitalize"
                                                                            >
                                                                                {
                                                                                    sector
                                                                                }
                                                                            </SelectItem>
                                                                        )
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <section className="flex w-full justify-end">
                                                <Button
                                                    className="px-6"
                                                    type="submit"
                                                    disabled={
                                                        isApplicationUpdating ||
                                                        isSubmitted
                                                    }
                                                >
                                                    Save Changes
                                                </Button>
                                            </section>
                                        </form>
                                    </Form>
                                </CardContent>
                            </Card>
                            <Card
                                id="applicationDetails"
                                x-chunk="dashboard-04-chunk-2"
                            >
                                <CardHeader>
                                    <CardTitle>Application Details</CardTitle>
                                    <CardDescription>
                                        Tell us more about your reason for
                                        applying for a grant
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Form {...applicationDetailsForm}>
                                        <form
                                            onSubmit={applicationDetailsForm.handleSubmit(
                                                onSubmitApplicationDetails
                                            )}
                                            className="space-y-8"
                                        >
                                            <FormField
                                                control={
                                                    applicationDetailsForm.control
                                                }
                                                name="requestedAmount"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Requested Amount
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                type="number"
                                                                min={0}
                                                                placeholder="Requested Amount"
                                                                {...field}
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={
                                                    applicationDetailsForm.control
                                                }
                                                name="applicationReason"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Reason for
                                                            Application
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Textarea
                                                                placeholder="Reason for Application"
                                                                {...field}
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={
                                                    applicationDetailsForm.control
                                                }
                                                name="orgAims"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            Aims of the
                                                            Organisation
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Textarea
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                                placeholder="Aims of the Organisation"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={
                                                    applicationDetailsForm.control
                                                }
                                                name="hearOf"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            How did you hear
                                                            about us?
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Select
                                                                onValueChange={
                                                                    field.onChange
                                                                }
                                                                value={
                                                                    field.value
                                                                }
                                                                disabled={
                                                                    isSubmitted
                                                                }
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="How did you hear about us?" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {hearOf.map(
                                                                        (
                                                                            source
                                                                        ) => (
                                                                            <SelectItem
                                                                                key={
                                                                                    source
                                                                                }
                                                                                value={
                                                                                    source
                                                                                }
                                                                            >
                                                                                {
                                                                                    source
                                                                                }
                                                                            </SelectItem>
                                                                        )
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <section className="flex w-full justify-end">
                                                <Button
                                                    className="px-6"
                                                    type="submit"
                                                    disabled={
                                                        isApplicationUpdating ||
                                                        isSubmitted
                                                    }
                                                >
                                                    Save Changes
                                                </Button>
                                            </section>
                                        </form>
                                    </Form>
                                </CardContent>
                            </Card>
                            <Card id="#" x-chunk="dashboard-04-chunk-2">
                                <CardHeader>
                                    <CardTitle>Upload Files</CardTitle>
                                    <CardDescription>
                                        Upload all necessary documents for your
                                        application
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="flex flex-col justify-start items-start gap-4">
                                    <section className="w-full flex flex-col justify-start items-start gap-4">
                                        {!isSubmitted && (
                                            <>
                                                <Label htmlFor="accountDocuments">
                                                    Annual Account Documents and
                                                    Additional Files
                                                </Label>
                                                <section
                                                    {...getRootProps()}
                                                    className="w-full min-h-20 flex items-center justify-center rounded-md border-2 border-dashed p-4 text-center cursor-pointer"
                                                >
                                                    <Input
                                                        {...getInputProps()}
                                                        className="hidden"
                                                        name="accountDocuments"
                                                        disabled={isSubmitted}
                                                    />
                                                    <section className="flex flex-col gap-2">
                                                        {accountFiles.length ===
                                                            0 && (
                                                            <p className="text-sm">
                                                                Drag and drop
                                                                your files here,
                                                                or click to
                                                                browse.
                                                            </p>
                                                        )}
                                                        {accountFiles.length >
                                                            0 &&
                                                            accountFiles.map(
                                                                ({
                                                                    file,
                                                                    isValid,
                                                                }) => (
                                                                    <div
                                                                        key={
                                                                            file.name
                                                                        }
                                                                        className="flex flex-col"
                                                                    >
                                                                        <div
                                                                            className="flex justify-center items-center gap-2 text-sm"
                                                                            style={{
                                                                                color: isValid
                                                                                    ? "inherit"
                                                                                    : "red",
                                                                            }}
                                                                        >
                                                                            <span>
                                                                                {
                                                                                    file.name
                                                                                }
                                                                            </span>
                                                                            <Button
                                                                                variant="ghost"
                                                                                onClick={(
                                                                                    e
                                                                                ) => {
                                                                                    e.preventDefault()
                                                                                    e.stopPropagation()
                                                                                    removeFile(
                                                                                        file.name
                                                                                    )
                                                                                }}
                                                                            >
                                                                                <X
                                                                                    size={
                                                                                        16
                                                                                    }
                                                                                />
                                                                            </Button>
                                                                        </div>
                                                                        {!isValid && (
                                                                            <span className="text-xs text-red-600">
                                                                                This
                                                                                file
                                                                                type
                                                                                cannot
                                                                                be
                                                                                uploaded.
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )
                                                            )}
                                                    </section>
                                                </section>
                                            </>
                                        )}
                                    </section>
                                    <section className="mt-4 flex flex-col justify-start items-start gap-2">
                                        <p className="font-semibold text-sm">
                                            Uploaded Files
                                        </p>
                                        {applicationData?.applicantFiles?.map(
                                            (file) => (
                                                <p
                                                    key={file.name}
                                                    className=" truncate max-w-[200px]"
                                                >
                                                    {file.name}
                                                </p>
                                            )
                                        )}
                                    </section>
                                    <section className="flex w-full justify-end">
                                        <Button
                                            className="px-6 justify-center items-center gap-2"
                                            onClick={handleFileUpload}
                                            disabled={
                                                isFileUploadPending ||
                                                isSubmitted
                                            }
                                        >
                                            <span>Save Changes</span>
                                            {isFileUploadPending && (
                                                <Loader
                                                    size={16}
                                                    className="animate-spin"
                                                />
                                            )}
                                        </Button>
                                    </section>
                                </CardContent>
                            </Card>
                            <Card
                                id="submitApplication"
                                x-chunk="dashboard-04-chunk-2"
                            >
                                <CardHeader>
                                    <CardTitle>
                                        Complete Application Submission
                                    </CardTitle>
                                    <CardDescription>
                                        Review your application details and
                                        ensure everything is correct before
                                        submitting.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="flex flex-wrap justify-end items-center gap-4">
                                    <Link to={APP_ROUTES.ROOT.DASHBOARD}>
                                        <Button variant="outline">
                                            Back Home
                                        </Button>
                                    </Link>
                                    <Button
                                        disabled={
                                            isApplicationUpdating ||
                                            isFileUploadPending ||
                                            isSubmitted
                                        }
                                        onClick={onSubmitApplicationForReview}
                                    >
                                        <span>
                                            Submit Application for Review
                                        </span>
                                        {isApplicationUpdating && (
                                            <Loader
                                                size={16}
                                                className="animate-spin"
                                            />
                                        )}
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </main>
            </div>
        </>
    )
}
